import { useState, useEffect, useCallback } from "react";
import api from "../api";

import {
  Container,
  Grid,
  Card,
  Typography,
  TextField,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend
} from "recharts";

export default function ManagerDashboard() {

  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [mwFilter, setMwFilter] = useState("");
  const [siteCountFilter, setSiteCountFilter] = useState("");

  const [yearList, setYearList] = useState([]);
  const [period, setPeriod] = useState("");
  const [upcoming, setUpcoming] = useState([]);
  const [amcStatus, setAmcStatus] = useState("live"); // ✅ NEW
  const [selectedMake, setSelectedMake] = useState(null);
  const [makeType, setMakeType] = useState(null); // 'scada' | 'plc'

  // ===============================
  // YEAR GENERATION
  // ===============================
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 2; i <= currentYear + 5; i++) {
      years.push(`${i}-${i + 1}`);
    }
    setYearList(years);
    setPeriod("all"); // ✅ default All
  }, []);

  // ===============================
  // FETCH DATA
  // ===============================


  const fetchSites = useCallback(async (year) => {
    try {
      const res = await api.get("/amc", {
        params: {
          year: year || undefined,
          status: amcStatus
        }
      });

      const data = res.data || [];
      setSites(data);
      setFilteredSites(data);
      setSelectedCustomer(null);
    } catch (err) {
      console.error("Failed to load sites:", err);
    }
  }, [amcStatus]);

  const fetchUpcoming = useCallback(async (year) => {
    try {
      const res = await api.get("/amc/upcoming", {
        params: { year: year || undefined }
      });

      setUpcoming(res.data);
    } catch (err) {
      console.error("Upcoming AMC Error:", err);
    }
  }, []);

    useEffect(() => {
    if (period === "") return;

    const year = period === "all" ? "" : period.split("-")[0];

    fetchSites(year);
    fetchUpcoming(year);
  }, [period, amcStatus, fetchSites, fetchUpcoming]);

  // SAFE DATE FORMAT
  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    if (isNaN(d)) return "N/A";
    const day   = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year  = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // TOTAL COUNTS
  // Count distinct plants (sites) per customer so a multi-year AMC is not
  // counted once per year. E.g. 2 sites × 5 years should show 2 sites, not 10.
  const distinctSites = (arr) => {
    const seen = new Set();
    return arr.filter((s) => {
      const key = s.plant_name || "";
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const totalSites     = distinctSites(sites).length;
  const totalCustomers = [...new Set(sites.map(s => s.customer_name))].length;
  const totalMW        = distinctSites(sites).reduce(
    (sum, s) => sum + parseFloat(s.plantcapacity_mw || 0), 0
  );

  // SCADA / PLC make counts (distinct plants per make)
  // Supports comma-separated multiple makes per site, e.g. "ABB, Siemens, Phoenix"
  const scadaCounts = Object.entries(
    distinctSites(sites).reduce((acc, s) => {
      const makes = (s.scada_make || "")
        .split(",")
        .map((m) => m.trim())
        .filter(Boolean);
      if (makes.length === 0) {
        acc["Not Specified"] = (acc["Not Specified"] || 0) + 1;
      } else {
        makes.forEach((make) => {
          acc[make] = (acc[make] || 0) + 1;
        });
      }
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]);

  const plcCounts = Object.entries(
    distinctSites(sites).reduce((acc, s) => {
      const makes = (s.plc_make || "")
        .split(",")
        .map((m) => m.trim())
        .filter(Boolean);
      if (makes.length === 0) {
        acc["Not Specified"] = (acc["Not Specified"] || 0) + 1;
      } else {
        makes.forEach((make) => {
          acc[make] = (acc[make] || 0) + 1;
        });
      }
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]);

  // Sites for selected make (click a make in the counts below to view its sites)
  const makeSites = selectedMake
    ? distinctSites(sites).filter((s) => {
        const field = makeType === "scada" ? "scada_make" : "plc_make";
        const makes = (s[field] || "")
          .split(",")
          .map((m) => m.trim())
          .filter(Boolean);
        return makes.includes(selectedMake);
      })
    : [];

  const handleMakeClick = (make, type) => {
    setSelectedMake(make);
    setMakeType(type);
  };

  // GROUP DATA FOR CHART — count distinct plants per customer
  const grouped = filteredSites.reduce((acc, site) => {
    const customer = site.customer_name || "Unknown";
    if (!acc[customer]) acc[customer] = [];
    acc[customer].push(site);
    return acc;
  }, {});

  const chartData = Object.entries(grouped).map(([customer, customerSites]) => {
    const unique = distinctSites(customerSites);
    const totalMW = unique.reduce(
      (sum, s) => sum + parseFloat(s.plantcapacity_mw || 0), 0
    );
    return { customer, sites: unique.length, mw: totalMW };
  });

  // FILTER
  const applyFilter = () => {
    let data = [...sites];

    if (mwFilter) {
      data = data.filter(
        (s) => parseFloat(s.plantcapacity_mw || 0) >= parseFloat(mwFilter)
      );
    }

    if (siteCountFilter) {
      const groupedTemp = data.reduce((acc, s) => {
        const cust = s.customer_name || "Unknown";
        if (!acc[cust]) acc[cust] = [];
        acc[cust].push(s);
        return acc;
      }, {});

      data = Object.values(groupedTemp)
        .filter((sites) => sites.length >= parseInt(siteCountFilter))
        .flat();
    }

    setFilteredSites(data);
    setSelectedCustomer(null);
  };

  // BAR CLICK
  const handleBarClick = (data) => {
    if (!data || !data.customer) return;
    const customer = data.customer;
    const sitesOfCustomer = distinctSites(
      filteredSites.filter((s) => s.customer_name === customer)
    );
    setSelectedCustomer({ name: customer, sites: sitesOfCustomer });
  };

  const handleExport = async () => {
    try {
      const year = period === "all" ? "" : period.split("-")[0];
      const response = await api.get("/amc/export", {
        params: {
          year: year || undefined,
          status: amcStatus           // ✅ pass status to export
        },
        responseType: "blob"
      });
      const url  = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href  = url;
      link.setAttribute("download", `AMC_Report_${amcStatus}_${year || "all"}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Export error:", err);
      alert("Failed to export data");
    }
  };

  return (
    <Container maxWidth="lg">

      {/* TITLE */}
      <Typography variant="h4" gutterBottom>
        Manager Dashboard —{" "}
        {amcStatus === "live" ? "🟢 Live" : amcStatus === "completed" ? "🔴 Completed" : "📋 All"}
        {" "}({period === "all" ? "All Years" : period})
      </Typography>

      {/* YEAR SELECT */}
      <Box sx={{ mb: 3, width: 250 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Period</InputLabel>
          <Select
            value={period}
            label="Period"
            onChange={(e) => setPeriod(e.target.value)}
          >
            <MenuItem value="all">All Years</MenuItem>  {/* ✅ All option */}
            {yearList.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* AMC STATUS TOGGLE */}
      <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center" }}>
        <Button
          variant={amcStatus === "live" ? "contained" : "outlined"}
          color="success"
          onClick={() => setAmcStatus("live")}
        >
          🟢 Live AMC
        </Button>
        <Button
          variant={amcStatus === "completed" ? "contained" : "outlined"}
          color="error"
          onClick={() => setAmcStatus("completed")}
        >
          🔴 Completed AMC
        </Button>
        <Button
          variant={amcStatus === "all" ? "contained" : "outlined"}
          color="inherit"
          onClick={() => setAmcStatus("all")}
        >
          📋 All
        </Button>
      </Box>

      {/* AMC ENDING ALERT */}
      {upcoming.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="error">
            {upcoming.length} AMC(s) ending soon:{" "}
            {upcoming.map((u) => u.plant_name).join(", ")}
            {upcoming.length > 6 && "..."}
          </Alert>
        </Box>
      )}

      {/* DASHBOARD CARDS */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6">Total MW</Typography>
            <Typography variant="h4" color="success.main">
              {totalMW.toFixed(2)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6">Total Sites</Typography>
            <Typography variant="h4" color="primary">
              {totalSites}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6">Total Customers</Typography>
            <Typography variant="h4" color="secondary">
              {totalCustomers}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* SCADA / PLC Make Counts */}
      <Card sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          SCADA / PLC Make — Plant Count
        </Typography>
        {selectedMake && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="text"
              onClick={() => {
                setSelectedMake(null);
                setMakeType(null);
              }}
              startIcon={<ArrowBackIcon />}
            >
              Back to all makes
            </Button>
            <Typography variant="subtitle2" color="text.secondary" sx={{ ml: 2, display: "inline-block" }}>
              Sites using {makeType === "scada" ? "SCADA" : "PLC"} make: <b>{selectedMake}</b>
            </Typography>
          </Box>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              SCADA Make
            </Typography>
            <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
              {scadaCounts.length > 0 ? (
                scadaCounts.map(([make, count]) => (
                  <Box
                    key={make}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 1,
                      borderBottom: 1,
                      borderColor: "divider",
                      cursor: selectedMake === make && makeType === "scada" ? "default" : "pointer",
                    }}
                    onClick={() => handleMakeClick(make, "scada")}
                  >
                    <Typography
                      color={
                        selectedMake === make && makeType === "scada"
                          ? "primary"
                          : "text.secondary"
                      }
                      fontWeight={
                        selectedMake === make && makeType === "scada" ? "bold" : "normal"
                      }
                    >
                      {make}
                    </Typography>
                    <Typography variant="h6">{count}</Typography>
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary">No SCADA make recorded</Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              PLC Make
            </Typography>
            <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
              {plcCounts.length > 0 ? (
                plcCounts.map(([make, count]) => (
                  <Box
                    key={make}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 1,
                      borderBottom: 1,
                      borderColor: "divider",
                      cursor: selectedMake === make && makeType === "plc" ? "default" : "pointer",
                    }}
                    onClick={() => handleMakeClick(make, "plc")}
                  >
                    <Typography
                      color={
                        selectedMake === make && makeType === "plc"
                          ? "primary"
                          : "text.secondary"
                      }
                      fontWeight={
                        selectedMake === make && makeType === "plc" ? "bold" : "normal"
                      }
                    >
                      {make}
                    </Typography>
                    <Typography variant="h6">{count}</Typography>
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary">No PLC make recorded</Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Sites for selected make */}
      {selectedMake && makeSites.length > 0 && (
        <Card sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Sites — {makeType === "scada" ? "SCADA" : "PLC"} Make: {selectedMake}
          </Typography>
          <TableContainer component={Paper}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {["Plant Name", "Customer", "SCADA Make", "PLC Make", "AMC Start", "AMC End", "PO Number"].map(
                    (head) => (
                      <TableCell
                        key={head}
                        sx={{
                          backgroundColor: "#1976d2 !important",
                          color: "#fff !important",
                          fontWeight: "bold",
                        }}
                      >
                        {head}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {makeSites.map((s) => (
                  <TableRow key={s.id} sx={{ cursor: "pointer" }}>
                    <TableCell>{s.plant_name}</TableCell>
                    <TableCell>{s.customer_name}</TableCell>
                    <TableCell>{s.scada_make || "N/A"}</TableCell>
                    <TableCell>{s.plc_make || "N/A"}</TableCell>
                    <TableCell>
                      {s.amc_start_date ? new Date(s.amc_start_date).toLocaleDateString("en-IN") : "N/A"}
                    </TableCell>
                    <TableCell>
                      {s.amc_end_date ? new Date(s.amc_end_date).toLocaleDateString("en-IN") : "N/A"}
                    </TableCell>
                    <TableCell>{s.po_number || "N/A"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* FILTER */}
      <Card sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Minimum MW"
              type="number"
              value={mwFilter}
              onChange={(e) => setMwFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Minimum Sites"
              type="number"
              value={siteCountFilter}
              onChange={(e) => setSiteCountFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" gap={2}>
              <Button variant="contained" onClick={applyFilter}>
                Apply Filter
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setMwFilter("");
                  setSiteCountFilter("");
                  setFilteredSites(sites);
                  setSelectedCustomer(null);
                }}
              >
                Clear
              </Button>
              <Button variant="contained" color="success" onClick={handleExport}>
                Export Excel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* CHART */}
      <Card sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Customer Portfolio Overview
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Site count and managed MW by customer. Select a bar to view its sites.
        </Typography>
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={chartData} margin={{ bottom: 80, right: 20 }}>
            <CartesianGrid stroke="#eaecf0" vertical={false} />
            <XAxis
              dataKey="customer"
              tick={{ fontSize: 12 }}
              angle={-35}
              textAnchor="end"
              interval={0}
            />
            <YAxis yAxisId="sites" allowDecimals={false} label={{ value: "Sites", angle: -90, position: "insideLeft" }} />
            <YAxis yAxisId="mw" orientation="right" label={{ value: "MW", angle: 90, position: "insideRight" }} />
            <Tooltip formatter={(value, name) => [Number(value).toLocaleString("en-IN"), name]} />
            <Legend verticalAlign="top" height={32} />
            <Bar yAxisId="sites" dataKey="sites" name="Sites" fill="#155eef" radius={[6, 6, 0, 0]} onClick={handleBarClick} />
            <Line yAxisId="mw" type="monotone" dataKey="mw" name="Managed MW" stroke="#f79009" strokeWidth={3} dot={{ r: 4 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      {/* SITE TABLE */}
      {selectedCustomer && (
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Sites for {selectedCustomer.name}
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1200 }} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Plant Name</TableCell>
                  <TableCell align="center">MW</TableCell>
                  <TableCell>SCADA Make</TableCell>
                  <TableCell>PLC Make</TableCell>
                  <TableCell>Billing Address</TableCell>
                  <TableCell>Contact Person</TableCell>
                  <TableCell align="center">PO No</TableCell>
                  <TableCell align="center">AMC Start</TableCell>
                  <TableCell align="center">AMC End</TableCell>
                  <TableCell align="center">Billing Cycle</TableCell>
                  <TableCell>Supporting Document</TableCell>
                  <TableCell align="center">Site Visit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedCustomer.sites.map((site) => (
                  <TableRow key={site.id}>
                    <TableCell>{site.plant_name}</TableCell>
                    <TableCell align="center">{site.plantcapacity_mw || "0"}</TableCell>
                    <TableCell>{site.scada_make || "N/A"}</TableCell>
                    <TableCell>{site.plc_make || "N/A"}</TableCell>
                    <TableCell>{site.billing_address?.split(",")[0] || "N/A"}</TableCell>
                    <TableCell>{site.contact_person || "N/A"}</TableCell>
                    <TableCell align="center">{site.po_number || "N/A"}</TableCell>
                    <TableCell align="center">{formatDate(site.amc_start_date)}</TableCell>
                    <TableCell align="center">{formatDate(site.amc_end_date)}</TableCell>
                    <TableCell align="center">{site.billing_cycle || "N/A"}</TableCell>
                    <TableCell>{site.supporting_document_for_invoice || "N/A"}</TableCell>
                    <TableCell align="center">{site.site_visit || "N/A"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Card>
      )}

    </Container>
  );
}
